import * as React from 'react';
import clsx from 'clsx';
import { constantFormat } from '../../../helpers/constants';
import { HistoryEventObjectTypeVals } from '../../../helpers/constants/modelValues/HistoryEventObjectTypeVals';
import { HistoryEventTypeVals } from '../../../helpers/constants/modelValues/HistoryEventTypeVals';
import { useBackgroundPromiseAllWrapper } from '../../../helpers/hooks/useBackgroundPromiseWrapper';
import { useIsMobile } from '../../../helpers/hooks/useIsMobile';
import { services } from '../../../services';
import { HistoryEvent } from '../models/HistoryEvent';
import styles from './KADocHistoryDefault.module.css';

interface KADocHistoryDefaultProps {
    projectDocId: number;
    folderIds: number[];
}

const getNestedVal = (obj: any, path: string): string => {
    const val = path.split('.').reduce((o, k) => o?.[k], obj);
    return val ?? '';
};

export const KADocHistoryDefault: React.FC<KADocHistoryDefaultProps> = ({ projectDocId, folderIds }) => {
    const [promiseAll, loading] = useBackgroundPromiseAllWrapper<[HistoryEvent[], HistoryEvent[]]>();
    const folderIdsKey = folderIds.join('|');
    const [data, setData] = React.useState<any[]>([]);
    const isMobile = useIsMobile(860);

    const [sortField, setSortField] = React.useState<string | null>(null);
    const [sortAsc, setSortAsc] = React.useState(true);

    const loadData = React.useCallback(() => {
        if (!projectDocId) return;

        const calls = [
            () =>
                services.historyEventService.fetch({
                    ObjectIds: [projectDocId],
                    ObjectTypeIds: [HistoryEventObjectTypeVals.ProjectDoc],
                    EventTypeIds: [
                        HistoryEventTypeVals.SentToContractor,
                        HistoryEventTypeVals.StartWorkflowInstance,
                        HistoryEventTypeVals.ApprovalProjectDoc,
                        HistoryEventTypeVals.CreatedAttachment,
                        HistoryEventTypeVals.RejectAgreement
                    ]
                }),
            ...(folderIds.length > 0
                ? [
                      () =>
                          services.historyEventService.fetch({
                              ObjectIds: folderIds,
                              ObjectTypeIds: [HistoryEventObjectTypeVals.ProjectDocFolder],
                              EventTypeIds: [
                                  HistoryEventTypeVals.SendingProjectDocGIPApproval,
                                  HistoryEventTypeVals.RejectAgreement,
                                  HistoryEventTypeVals.EdiSigningCompleted,
                                  HistoryEventTypeVals.RejectProjectDocGIPApproval,
                                  HistoryEventTypeVals.SentVolume
                              ]
                          })
                  ]
                : [])
        ];

        promiseAll(calls, ([doc, folders]) => {
            const merged = [...(doc ?? []), ...(folders ?? [])].sort((a, b) => {
                const aTime = a.Created ? new Date(a.Created).getTime() : 0;
                const bTime = b.Created ? new Date(b.Created).getTime() : 0;
                return bTime - aTime;
            });
            merged.forEach((x) => {
                x['HistoryCreated'] = constantFormat.DateTimeToString(x.Created ? new Date(x.Created) : undefined);
            });
            setData(merged);
        });
    }, [projectDocId, folderIdsKey, promiseAll]);

    React.useEffect(loadData, [projectDocId, folderIdsKey]);

    const sorted = React.useMemo(() => {
        if (!sortField) return data;
        return [...data].sort((a, b) => {
            const av = getNestedVal(a, sortField);
            const bv = getNestedVal(b, sortField);
            return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
        });
    }, [data, sortField, sortAsc]);

    const onSort = (field: string) => {
        if (sortField === field) setSortAsc(a => !a);
        else { setSortField(field); setSortAsc(true); }
    };

    const sortIcon = (field: string) => {
        if (sortField !== field) return 'fa-solid fa-sort';
        return sortAsc ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down';
    };

    const COLUMNS = [
        { field: 'EventType.Title', label: 'Тип' },
        { field: 'Title',           label: 'Описание' },
        { field: 'CreatedBy.Title', label: 'Инициатор' },
        { field: 'HistoryCreated',  label: 'Время' },
    ];

    return (
        <div className={styles.wrap}>
            {loading && (
                <div className={styles.loading}>
                    <i className="fa-solid fa-spinner fa-spin" />
                </div>
            )}

            {!loading && data.length === 0 && (
                <div className={styles.empty}>Нет событий</div>
            )}

            {!loading && data.length > 0 && (
                isMobile ? (
                    <>
                        <div className={styles.cards}>
                            {sorted.map(h => (
                                <div key={h.Id} className={styles.card}>
                                    <div className={styles.cardType}>{h.EventType?.Title}</div>
                                    {h.Title && <div className={styles.cardDesc}>{h.Title}</div>}
                                    <div className={styles.cardFooter}>
                                        <span className={styles.cardAuthor}>
                                            <i className="fa-regular fa-user" />
                                            {h.CreatedBy?.Title}
                                        </span>
                                        <span className={styles.cardTime}>{h.HistoryCreated}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.header}>
                            {COLUMNS.map(col => (
                                <button
                                    key={col.field}
                                    className={styles.headerCell}
                                    onClick={() => onSort(col.field)}
                                >
                                    {col.label}
                                    <i className={sortIcon(col.field)} />
                                </button>
                            ))}
                        </div>
                        <div>
                            {sorted.map(h => (
                                <div key={h.Id} className={styles.row}>
                                    <div className={clsx(styles.colType)}>{h.EventType?.Title}</div>
                                    <div className={clsx(styles.colDesc)}>{h.Title}</div>
                                    <div className={clsx(styles.colAuthor)}>{h.CreatedBy?.Title}</div>
                                    <div className={clsx(styles.colTime)}>{h.HistoryCreated}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )
            )}
        </div>
    );
};
